import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { CRUDService } from '../CRUDService.api';
import httpClient from '../httpClient';
import {
  AccountingEntry,
  AccountingEntryDetail,
  AccountingEntryGroup,
  AccountingEntryPayload,
  AccountingEntrySummary,
} from './accountingEntry.model';
import * as Sentry from '@sentry/react';
import {
  AccountingEntryByLAQueryParams,
  AccountingEntryQueryParams,
  QueryRequestParams,
  ResponsePaginated,
} from '../types';
import { LedgerAccountApi } from '../ledgerAccount/ledgerAccount.model';
import {
  DEFAULT_PAGINATION_PAGE_INDEX,
  DEFAULT_PAGINATION_PAGE_SIZE,
  DEFAULT_PAGINATION_SORT,
  DEFAULT_PAGINATION_DIR,
  DEFAULT_PAGINATION_PAGE_SEARCH,
} from '@/common/constants';
import { AccountingEntryFormType } from '@/pages/AccountsMayor/AccountingEntry/AccountingEntrySchema';
import { UUID } from 'crypto';
import { getCurrentDate, getFormattedDate } from '@/lib/utils';

const ACCOUNTING_ENTRY_BASE_PATH = '/accountingEntry';
const ACCOUNTING_ENTRY_KEY = 'accountingEntry';
const ACCOUNTING_ENTRY_STALE_TIME = 5000;

const resolveEntityId = (...ids: Array<string | null | undefined>) => {
  return ids.find((id): id is string => Boolean(id));
};

const getAccountingEntryAmount = (
  accountingEntry: AccountingEntryFormType['accountingEntries'][number],
) => {
  return accountingEntry.currency === 'ARS'
    ? Math.abs(accountingEntry.amount)
    : Math.abs(accountingEntry.amount) * accountingEntry.exchangeRateOfficial;
};

const getMoneyPayload = (
  accountingEntry: AccountingEntryFormType['accountingEntries'][number],
  exchangeRate: number,
) => {
  if (accountingEntry.currency === 'ARS') {
    return undefined;
  }

  return {
    currency: accountingEntry.currency,
    amount: Math.abs(accountingEntry.amount),
    exchangeRate,
  };
};

const getMoneyAltPayload = (
  accountingEntry: AccountingEntryFormType['accountingEntries'][number],
) => {
  const exchangeRateAlt = accountingEntry.exchangeRateAlt || 0;

  if (accountingEntry.currency === 'ARS') {
    if (exchangeRateAlt > 0 && exchangeRateAlt !== 1) {
      return {
        currency: 'USD',
        amount: Math.abs(accountingEntry.amount) / exchangeRateAlt,
        exchangeRate: exchangeRateAlt,
      };
    }
    return undefined;
  }

  return {
    currency: accountingEntry.currency,
    amount: Math.abs(accountingEntry.amount),
    exchangeRate: exchangeRateAlt,
  };
};

const transformAccountingEntriesPayload = (
  data: AccountingEntryFormType,
): Array<AccountingEntryPayload> => {
  return data.accountingEntries.map((accountingEntry) => {
    const donationId = resolveEntityId(accountingEntry.donation?.id, data.donationId);
    const costCenterId = resolveEntityId(accountingEntry.costCenter?.id);
    const categoryId = resolveEntityId(accountingEntry.category?.id);

    return {
      ledgerAccount: { id: accountingEntry.ledgerAccount.id as LedgerAccountApi['id'] },
      paymentDate: data.paymentDate.toISOString().split('T')[0],
      amount: getAccountingEntryAmount(accountingEntry),
      credit: accountingEntry.amount < 0 ? true : false,
      details: accountingEntry.details,
      moneyOfficial: getMoneyPayload(accountingEntry, accountingEntry.exchangeRateOfficial),
      moneyAlt: getMoneyAltPayload(accountingEntry),
      transaction: data.transactionId
        ? { id: data.transactionId as UUID, type: 'DonationDetail' }
        : undefined,
      donation: donationId ? { id: donationId as UUID } : undefined,
      costCenter: costCenterId ? { id: costCenterId as UUID } : undefined,
      category: categoryId ? { id: categoryId as UUID } : undefined,
      fileURL: data.files && data.files.length > 0 ? data.files[0] : undefined,
    };
  });
};
class AccountingEntryService extends CRUDService<AccountingEntry, AccountingEntryFormType> {
  constructor() {
    super(ACCOUNTING_ENTRY_BASE_PATH);
  }

  async getList() {
    try {
      const response = await httpClient.get(`${ACCOUNTING_ENTRY_BASE_PATH}/list`);
      return response.data;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async getByLedgerAccount(ledgerAccountId: LedgerAccountApi['id'], queryParams?: string) {
    try {
      const response = await httpClient.get(
        queryParams
          ? `${ACCOUNTING_ENTRY_BASE_PATH}/ledgerAccount/${ledgerAccountId}?${queryParams}`
          : `${ACCOUNTING_ENTRY_BASE_PATH}/ledgerAccount/${ledgerAccountId}`,
      );
      return response.data;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async getByGroup(groupId: AccountingEntryGroup['id'], queryParams?: string) {
    try {
      const response = await httpClient.get(
        queryParams
          ? `${ACCOUNTING_ENTRY_BASE_PATH}/group/${groupId}?${queryParams}`
          : `${ACCOUNTING_ENTRY_BASE_PATH}/group/${groupId}`,
      );
      return response.data;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async postList(data: Array<AccountingEntryPayload>) {
    try {
      await httpClient.post(`${ACCOUNTING_ENTRY_BASE_PATH}/list`, { data });
      return;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }
}

const accountingEntryService = new AccountingEntryService();

const useAccountingEntries = ({
  page = DEFAULT_PAGINATION_PAGE_INDEX,
  size = DEFAULT_PAGINATION_PAGE_SIZE,
  sort = DEFAULT_PAGINATION_SORT,
  dir = DEFAULT_PAGINATION_DIR,
  search = DEFAULT_PAGINATION_PAGE_SEARCH,
  date = '',
}: AccountingEntryQueryParams<AccountingEntry> = {}) => {
  const dateParam = date ? `&date=${date}` : '';

  const queryParams = `page=${page}&size=${size}&sort=${sort}&dir=${dir}&query=${search}${dateParam}`;

  return useQuery<ResponsePaginated<AccountingEntrySummary>, Error>({
    queryKey: [ACCOUNTING_ENTRY_KEY, { page, size, search, date }],
    queryFn: async () => {
      const response = await accountingEntryService.getAll(queryParams);
      return response as ResponsePaginated<AccountingEntrySummary>;
    },
    placeholderData: keepPreviousData,
    staleTime: ACCOUNTING_ENTRY_STALE_TIME,
  });
};

const useAccountingEntriesByLedgerAccount = ({
  page = DEFAULT_PAGINATION_PAGE_INDEX,
  size = DEFAULT_PAGINATION_PAGE_SIZE,
  sort = DEFAULT_PAGINATION_SORT,
  dir = DEFAULT_PAGINATION_DIR,
  ledgerAccountId,
  startDate = '',
  endDate = '',
}: AccountingEntryByLAQueryParams<LedgerAccountApi> & {
  ledgerAccountId: LedgerAccountApi['id'];
}) => {
  const dateParam = startDate && endDate ? `&startDate=${startDate}&endDate=${endDate}` : '';

  const queryParams = `page=${page}&size=${size}&sort=${sort}&dir=${dir}${dateParam}`;

  return useQuery<ResponsePaginated<AccountingEntryDetail>, Error>({
    queryKey: [ACCOUNTING_ENTRY_KEY, { page, size, ledgerAccountId, startDate, endDate }],
    queryFn: async () => {
      const response = await accountingEntryService.getByLedgerAccount(
        ledgerAccountId,
        queryParams,
      );
      return response as ResponsePaginated<AccountingEntryDetail>;
    },
    placeholderData: keepPreviousData,
    staleTime: ACCOUNTING_ENTRY_STALE_TIME,
    enabled: !!ledgerAccountId,
  });
};

const useAccountingEntriesByGroup = ({
  page = DEFAULT_PAGINATION_PAGE_INDEX,
  size = DEFAULT_PAGINATION_PAGE_SIZE,
  sort = DEFAULT_PAGINATION_SORT,
  dir = DEFAULT_PAGINATION_DIR,
  groupId,
}: QueryRequestParams<AccountingEntryGroup> & { groupId: AccountingEntryGroup['id'] }) => {
  const queryParams = `page=${page}&size=${size}&sort=${sort}&dir=${dir}`;

  return useQuery<ResponsePaginated<AccountingEntryDetail>, Error>({
    queryKey: [ACCOUNTING_ENTRY_KEY, { page, size, groupId }],
    queryFn: async () => {
      const response = await accountingEntryService.getByGroup(groupId, queryParams);
      return response as ResponsePaginated<AccountingEntryDetail>;
    },
    placeholderData: keepPreviousData,
    staleTime: ACCOUNTING_ENTRY_STALE_TIME,
    enabled: !!groupId,
  });
};

const useGetAccountingReport = (params?: URLSearchParams) => {
  const date = params?.get('date');
  const queryParams = date ? `?date=${date}` : '';

  return async () => {
    const response = await httpClient.get(`accountingEntry/all/xlsx${queryParams}`, {
      responseType: 'blob',
      timeout: 600000,
    });

    const blob = new Blob([response.data as BlobPart], {
      type: response.headers['content-type'],
    });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `MayorDeCuentas-PEM_${getCurrentDate()}.xlsx`);
    link.click();
  };
};

const useGetAccountingReportByLedgerAccount = (id: string, params?: URLSearchParams) => {
  const query = new URLSearchParams();
  const startDate = params?.get('startDate');
  const endDate = params?.get('endDate');

  if (startDate) query.set('startDate', startDate);
  if (endDate) query.set('endDate', endDate);

  const queryParams = query.toString() ? `?${query.toString()}` : '';

  return async () => {
    const response = await httpClient.get(
      `accountingEntry/ledgerAccount/${id}/xlsx${queryParams}`,
      {
        responseType: 'blob',
        timeout: 600000,
      },
    );

    const blob = new Blob([response.data as BlobPart], {
      type: response.headers['content-type'],
    });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `-PEM_${getCurrentDate()}.xlsx`);
    link.click();
  };
};

const useGetJournalBookReport = () => {
  return async (params?: { startDate?: Date; endDate?: Date }) => {
    const query = new URLSearchParams();
    if (params?.startDate) {
      query.set('startDate', getFormattedDate(params.startDate));
    }
    if (params?.endDate) {
      query.set('endDate', getFormattedDate(params.endDate));
    }

    const queryParams = query.toString() ? `?${query.toString()}` : '';

    const response = await httpClient.get(`accountingEntry/journal/pdf${queryParams}`, {
      responseType: 'blob',
      timeout: 600000,
    });

    const blob = new Blob([response.data as BlobPart], {
      type: response.headers['content-type'],
    });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `Libro-Diario-PEM_${getCurrentDate()}.pdf`);
    link.click();
  };
};

const useGetLedgerBookPdfReport = () => {
  return async (params?: { startDate?: Date; endDate?: Date }) => {
    const query = new URLSearchParams();
    if (params?.startDate) {
      query.set('startDate', getFormattedDate(params.startDate));
    }
    if (params?.endDate) {
      query.set('endDate', getFormattedDate(params.endDate));
    }

    const queryParams = query.toString() ? `?${query.toString()}` : '';

    const response = await httpClient.get(`accountingEntry/ledger/pdf${queryParams}`, {
      responseType: 'blob',
      timeout: 600000,
    });

    const blob = new Blob([response.data as BlobPart], {
      type: response.headers['content-type'],
    });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `Libro-Mayor-PEM_${getCurrentDate()}.pdf`);
    link.click();
  };
};

const useGetLedgerBookXlsxReport = () => {
  return async (params?: { startDate?: Date; endDate?: Date }) => {
    const query = new URLSearchParams();
    if (params?.startDate) {
      query.set('startDate', getFormattedDate(params.startDate));
    }
    if (params?.endDate) {
      query.set('endDate', getFormattedDate(params.endDate));
    }

    const queryParams = query.toString() ? `?${query.toString()}` : '';

    const response = await httpClient.get(`accountingEntry/ledger/xlsx${queryParams}`, {
      responseType: 'blob',
      timeout: 600000,
    });

    const blob = new Blob([response.data as BlobPart], {
      type: response.headers['content-type'],
    });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `Libro-Mayor-PEM_${getCurrentDate()}.xlsx`);
    link.click();
  };
};
const createAccountingEntryMutationFn = async (data: AccountingEntryFormType) => {
  return await accountingEntryService.create(data);
};

const createAccountingEntriesMutationFn = async (data: AccountingEntryFormType) => {
  const payload = transformAccountingEntriesPayload(data);
  return await accountingEntryService.postList(payload);
};

export {
  useAccountingEntries,
  useAccountingEntriesByLedgerAccount,
  useAccountingEntriesByGroup,
  useGetAccountingReport,
  createAccountingEntryMutationFn,
  createAccountingEntriesMutationFn,
  useGetAccountingReportByLedgerAccount,
  useGetJournalBookReport,
  useGetLedgerBookPdfReport,
  useGetLedgerBookXlsxReport,
};
